import {
  Component,
  signal,
  input,
  output,
  ChangeDetectionStrategy,
  inject,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmLabelDirective } from '@spartan-ng/helm/label';
import { HlmBadgeDirective } from '@spartan-ng/helm/badge';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideX, lucidePlus } from '@ng-icons/lucide';
import {
  ValidatorInput,
  ValidatorFn,
  normalizeValidator,
} from '@shared/utils/validators';

/**
 * Tag Input Component
 *
 * Reusable multi-select component for managing tags/emails with badges.
 *
 * Features:
 * - Add/remove tags with keyboard (Enter) and button support
 * - Configurable behavior (add, remove, select only)
 * - Optional validation for new items
 * - Existing items list for selection-only mode
 * - Automatic deduplication
 * - Accessible badge display
 *
 * Usage:
 * ```html
 * <app-tag-input
 *   [tags]="emailList()"
 *   (tagsChange)="emailList.set($event)"
 *   placeholder="name@unternehmen.de"
 *   [allowAdd]="true"
 *   [allowRemove]="true"
 *   [validation]="isValidEmail"
 *   badgeLabel="Email"
 * />
 * ```
 */
@Component({
  selector: 'app-tag-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HlmInputDirective,
    HlmButtonDirective,
    HlmBadgeDirective,
    NgIconComponent,
  ],
  providers: [provideIcons({ lucideX, lucidePlus })],
  templateUrl: './tag-input.html',
  styleUrl: './tag-input.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagInputComponent {
  // ───── Inputs ─────

  /** Current tags/items */
  readonly tags = input<string[]>([]);

  /** Placeholder text for input field */
  readonly placeholder = input('Add item...');

  /** Whether users can add new items */
  readonly allowAdd = input(true);

  /** Whether users can remove items */
  readonly allowRemove = input(true);

  /** List of existing items (for selection-only mode) */
  readonly existingItems = input<string[]>([]);

  /** If true, can only select from existingItems (no new items) */
  readonly allowSelectOnly = input(false);

  /** Validation function: returns true if valid, false/error message if invalid */
  readonly validation = input<ValidatorInput | null>(null);

  /** Label shown on badges (optional, for accessibility) */
  readonly badgeLabel = input('Tag');

  /** Disabled state */
  readonly disabled = input(false);

  /** Single-select mode (only one item allowed) */
  readonly singleSelect = input(false);

  // ───── Outputs ─────

  /** Emits entire updated tag array */
  readonly tagsChange = output<string[]>();

  /** Emits when a tag is added */
  readonly tagAdded = output<string>();

  /** Emits when a tag is removed */
  readonly tagRemoved = output<string>();

  // ───── State ─────

  inputValue = signal('');
  inputError = signal<string | null>(null);

  // ───── Computed ─────

  /** Tags to display (deduplicated) */
  readonly displayTags = computed(() => {
    const tagList = this.tags();
    return tagList.filter((t, i) => tagList.indexOf(t) === i);
  });

  /** Unselected items (for selection-only mode) */
  readonly unselectedItems = computed(() => {
    if (!this.allowSelectOnly()) return [];
    const selected = this.displayTags();
    return this.existingItems().filter((item) => !selected.includes(item));
  });

  /** Whether "Add" button should be enabled */
  readonly canAddButton = computed(() => {
    const value = this.inputValue().trim();
    if (!value || !this.allowAdd()) return false;
    if (this.allowSelectOnly()) return false;
    if (this.inputError()) return false;
    return true;
  });

  // ───── Validation Helper ─────

  /**
   * Validate an item value
   */
  validateItem(value: string): boolean | string {
    const validatorInput = this.validation();
    const validator = normalizeValidator(validatorInput);

    if (!validator) return true;

    const result = validator(value);
    return result === true || result === null ? true : result || false;
  }

  /**
   * Get error message for invalid item
   */
  getItemError(value: string): string | null {
    const result = this.validateItem(value);
    if (result === true || result === false) return null;
    return typeof result === 'string' ? result : 'Invalid item';
  }

  /**
   * Add a tag
   */
  addTag(value?: string): void {
    const itemToAdd = (value ?? this.inputValue()).trim();

    if (!itemToAdd) return;
    if (!this.allowAdd()) return;

    // Validate
    const error = this.getItemError(itemToAdd);
    if (error) {
      this.inputError.set(error);
      return;
    }

    // Check for duplicates
    if (this.displayTags().includes(itemToAdd)) {
      this.inputError.set('Already added');
      return;
    }

    // Single-select: replace instead of add
    if (this.singleSelect()) {
      const newTags = [itemToAdd];
      this.tagsChange.emit(newTags);
      this.tagAdded.emit(itemToAdd);
    } else {
      // Multi-select: append
      const newTags = [...this.displayTags(), itemToAdd];
      this.tagsChange.emit(newTags);
      this.tagAdded.emit(itemToAdd);
    }

    // Reset input
    this.inputValue.set('');
    this.inputError.set(null);
  }

  /**
   * Remove a tag
   */
  removeTag(tag: string): void {
    if (!this.allowRemove()) return;

    const newTags = this.displayTags().filter((t) => t !== tag);
    this.tagsChange.emit(newTags);
    this.tagRemoved.emit(tag);
  }

  /**
   * Handle input changes
   */
  onInputChange(value: string): void {
    this.inputValue.set(value);

    // Clear error on new input
    if (this.inputError() && value) {
      this.inputError.set(null);
    }

    // Validate as user types (if not select-only)
    if (!this.allowSelectOnly() && value.trim()) {
      const error = this.getItemError(value.trim());
      if (error) {
        this.inputError.set(error);
      }
    }
  }

  /**
   * Handle Enter key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
    }
  }

  /**
   * Select from existing items (click handler)
   */
  selectExistingItem(item: string): void {
    if (this.singleSelect()) {
      const newTags = [item];
      this.tagsChange.emit(newTags);
      this.tagAdded.emit(item);
    } else {
      const newTags = [...this.displayTags(), item];
      this.tagsChange.emit(newTags);
      this.tagAdded.emit(item);
    }
    this.inputValue.set('');
    this.inputError.set(null);
  }
}
